import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Stack, Typography } from '@optimus-web/core';
import { LocalizedText, lt } from '@mews/framework';
import { SelectField } from '@mews-commander/form';
import { Option } from '@mews-commander/types';
import { PortfolioProductCharging, ServiceType } from '@mews-commander/data-access';
import { getProductChargingTextKeyForBookableServices } from '@mews/business-components';
import { ProductChargingMode } from '@mews/enums';
import { getCurrentServiceFieldName, ServicesFieldNames } from '../../formEnums';
import { getPaymentChargingOptions, getConsumptionMomentOptions } from './paymentsCharging';

interface Props {
    isFetching: boolean;
    selectedService: string;
}
export const ServicesPayments = ({ selectedService, isFetching }: Props) => {
    const { watch, setValue } = useFormContext();

    // current form values
    const currentServiceType: ServiceType = watch(
        `${getCurrentServiceFieldName(selectedService)}.${ServicesFieldNames.ServiceType}`
    );
    const currentBookablePeriod =
        watch(`${getCurrentServiceFieldName(selectedService)}.${ServicesFieldNames.BookablePeriod}`) || '';

    // field names
    const chargingFieldName = `${getCurrentServiceFieldName(selectedService)}.${ServicesFieldNames.Charging}`;
    const consumptionFieldName = `${getCurrentServiceFieldName(selectedService)}.${
        ServicesFieldNames.ConsumptionMoment
    }`;

    const selectedOption: PortfolioProductCharging = watch(chargingFieldName);

    const [subOptions, setSubOptions] = useState<Array<Option> | null>(null);

    const options = getPaymentChargingOptions(currentServiceType, currentBookablePeriod).map((option) => ({
        label: (
            <LocalizedText
                textKey={getProductChargingTextKeyForBookableServices(
                    option as unknown as ProductChargingMode,
                    currentBookablePeriod
                )}
                substitutes={{ timeUnit: currentBookablePeriod }}
            />
        ),
        value: option,
    }));

    useEffect(() => {
        if (!selectedOption && options.length === 1) {
            setValue(chargingFieldName, options[0].value);
        }
    }, [chargingFieldName, selectedOption, options, setValue]);

    useEffect(() => {
        if (!selectedOption) return;

        const selectedOptionData = getConsumptionMomentOptions(
            currentServiceType,
            currentBookablePeriod,
            selectedOption
        );
        setSubOptions(selectedOptionData as Option[]);

        if (selectedOptionData.length === 1) {
            setValue(consumptionFieldName, selectedOptionData[0].value);
        }
    }, [consumptionFieldName, currentBookablePeriod, currentServiceType, selectedOption, setValue]);

    return (
        <>
            <Typography textStyle="bodyLargeStrong" as="div">
                {lt('ConfigureProductServicesSubHeadingPayments')}
            </Typography>
            <Stack spacing="50" distribution="stretch">
                <SelectField
                    disableInputSearch
                    required
                    readOnly={options.length === 1}
                    name={chargingFieldName}
                    options={options as Array<Option>}
                    label={lt('ConfigureProductServicesPaymentsCharging')}
                    loader={isFetching}
                />
                <SelectField
                    disableInputSearch
                    required
                    readOnly={subOptions?.length === 1}
                    name={consumptionFieldName}
                    options={subOptions ?? []}
                    label={lt('ConfigureProductServicesPaymentsTiming')}
                    loader={isFetching}
                    disabled={!selectedOption}
                />
            </Stack>
        </>
    );
};
